import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Button, Form, Alert, Badge } from 'react-bootstrap';
import editIcon from '../assets/icons/edit.svg';
import saveIcon from '../assets/icons/save.svg';
import closeIcon from '../assets/icons/close.svg';
import { AgentPermissionForm } from './AgentPermissionForm';

/**
 * UserDetail component displays and allows editing of a specific user
 * For agents, allows editing permissions (delete permission, viewable stores)
 */
export function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiCall, user: currentUser } = useOutletContext();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    loadUser();
  }, [id, apiCall]);

  const loadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const roleHint = searchParams.get('role');
      const params = roleHint ? `?role=${roleHint}` : '';
      const response = await apiCall(`${API_BASE_URL}/users/${id}${params}`);
      const userData = response.data || response;
      setUser(userData);
      setFormData(userData);
    } catch (err) {
      setError('Failed to load user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      await apiCall(`${API_BASE_URL}/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(formData),
      });
      setSuccessMessage('User updated successfully');
      setIsEditing(false);
      loadUser();
    } catch (err) {
      setError('Failed to update user: ' + err.message);
    }
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'agent':
        return 'primary';
      case 'customer':
        return 'secondary';
      default:
        return 'light';
    }
  };

  const formatRole = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  if (error && !user) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="link" onClick={() => navigate('/dashboard/settings/users')}>
          ← Back to Users
        </Button>
      </Container>
    );
  }

  const isAgent = user.role !== 'customer';

  return (
    <Container fluid className="py-4">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => navigate('/dashboard/settings/users')} className="p-0">
            ← Back to Users
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h2>
            {user.name}
            <span style={{ fontSize: '0.8em', marginLeft: '10px', color: '#666' }}>
              (ID: {user.id})
            </span>
          </h2>
        </Col>
      </Row>

      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      {!isEditing ? (
        <>
          <Card className="mb-4">
            <Card.Header>
              <h3>{user.role === 'customer' ? 'Customer' : 'Agent/Admin'} Information</h3>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                    Name
                  </strong>
                  <span>{user.name}</span>
                </Col>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                    Email
                  </strong>
                  <span>{user.email || '-'}</span>
                </Col>
                <Col md={6} className="mb-3">
                  <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                    Role
                  </strong>
                  <Badge bg={getRoleBadgeVariant(user.role)}>
                    {formatRole(user.role)}
                  </Badge>
                </Col>
                {user.phone && (
                  <Col md={6} className="mb-3">
                    <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                      Phone
                    </strong>
                    <span>{user.phone}</span>
                  </Col>
                )}
              </Row>

              {isAgent && (
                <>
                  <hr className="my-3" />
                  <h5 className="mb-3">Agent Permissions</h5>
                  <Row>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                        Primary Store
                      </strong>
                      <span>{user.store_name || '-'}</span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                        Delete Permission
                      </strong>
                      <Badge bg={user.can_delete ? 'success' : 'secondary'}>
                        {user.can_delete ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </Col>
                    <Col md={12} className="mb-3">
                      <strong className="d-block mb-1" style={{ fontSize: '0.7rem', color: '#666' }}>
                        Viewable Stores
                      </strong>
                      <span>
                        {user.view_store_ids && user.view_store_ids.length > 0
                          ? `${user.view_store_ids.length} store(s) selected`
                          : `Only primary store (${user.store_name || 'None'})`}
                      </span>
                    </Col>
                  </Row>
                </>
              )}
            </Card.Body>
          </Card>

          <Button
            variant="primary"
            onClick={() => setIsEditing(true)}
          >
            <img src={editIcon} alt="" style={{ width: '16px', marginRight: '8px' }} />
            Edit User
          </Button>
        </>
      ) : (
        <Card className="mb-4">
          <Card.Header>
            <h3>Edit {user.role === 'customer' ? 'Customer' : 'Agent/Admin'}</h3>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleSave}>
              <Row>
                {user.role === 'customer' ? (
                  <>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.first_name || ''}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.last_name || ''}
                          onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.email || ''}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.phone || ''}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.name || ''}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={formData.email || ''}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.phone || ''}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
              </Row>

              {isAgent && (
                <AgentPermissionForm
                  permissions={formData}
                  onChange={setFormData}
                />
              )}

              <div className="mt-3">
                <Button variant="primary" type="submit" className="me-2">
                  <img src={saveIcon} alt="" style={{ width: '16px', marginRight: '8px' }} />
                  Save Changes
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsEditing(false);
                    setFormData(user);
                    setError(null);
                  }}
                >
                  <img src={closeIcon} alt="" style={{ width: '16px', marginRight: '8px' }} />
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
